/**
 * Entry point — spec §B5 (`src/main.tsx`).
 * Import order matters: Leaflet's stylesheet first (§F5.1), then tokens before base, so
 * `base.css` can use the §F2 variables and override Leaflet's defaults.
 */
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import 'leaflet/dist/leaflet.css';
import './styles/tokens.css';
import './styles/base.css';
import './lib/i18n';
import { App } from './app/App';
import { hydrateFromHash } from './lib/permalink';
import { useAppStore } from './lib/state';

/**
 * e2e handle for the §F1 store, the sibling of `MapView`'s `window.__map`. §F5.11 asks
 * e2e to assert store-only outcomes (a click selecting a unit before §F5.7's panel exists,
 * the year the time bar moved to); reading them here beats inventing throwaway DOM.
 *
 * **Production ships `getState` + `subscribe` only** (NOTES #192): e2e runs against the
 * built bundle behind nginx, so the handle cannot be `DEV`-only, but nothing outside a dev
 * console needs `setState`. Client state is presentation state — the role in it gates UI
 * only and every §I1 rule is re-checked server-side (§D1/§I2) — so a reader here forges
 * nothing; keeping the *writer* out of the production bundle simply removes a pointless
 * "drive the app from the console" affordance. `import.meta.env.DEV` keeps the full
 * zustand hook (setState, direct subscription) for `pnpm dev` and vitest.
 */
export type StoreHandle = Pick<typeof useAppStore, 'getState' | 'subscribe'>;

declare global {
  interface Window {
    __store?: StoreHandle;
  }
}
window.__store = import.meta.env.DEV
  ? useAppStore
  : { getState: useAppStore.getState, subscribe: useAppStore.subscribe };

/**
 * §F1 "hydrate store from hash on boot" — **before** `createRoot`, deliberately.
 * Every §F1 query key is derived from this store (`['values', indicator, level, year,
 * country]`, `['layers', role]`, …), and they are issued from the first render's effects:
 * hydrating any later would fetch a full round of the *default* view before the shared one,
 * and §F5.11's "share URL restores full map state on load" would flash the wrong map first.
 * `hydrateFromHash` also corrects the path when the hash names another §F1 view, which has
 * to happen before `BrowserRouter` reads `window.location` (see lib/permalink.ts).
 */
hydrateFromHash(useAppStore);

const container = document.getElementById('root');
if (!container) throw new Error('#root missing from index.html');

createRoot(container).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
